import React, { useCallback, useState, useEffect } from "react";
import { Form, Row, Col, DatePicker, Select, message } from "antd";
import moment from "moment";
import { getAvailablePickup, getAvailableDropoff } from "@/utils/dates";
import { LoadTimeSlot, LoadTimeSlotMap } from "@/utils/enum";
import { getRestrictedDaysOfWeekAsString } from "@/utils/utils";
import { changeAdminETARequest } from "@/services/loads";
import ShipmentEtaCalendarFooter from "@/components/ShipmentEtaCalendarFooter";

function convertMilHours(hour) {
  if (hour === 0 || hour === 12) {
    return 12;
  }

  return hour % 12;
}

const CALENDAR_FOOTER = {
  PICKUP: "pickup",
  DROPOFF: "dropoff",
};

export default function ETAForm({ formRef, shipmentData, setIsLoadingDelivery, refreshData }) {
  const [pickupEtaDateSet, setPickupEtaDateSet] = useState(true);
  const [dropoffEtaDateSet, setDropoffEtaDateSet] = useState(true);
  const [validDropoffDates, setValidDropoffDates] = useState([]);
  const [validPickupDates, setValidPickupDates] = useState([]);

  useEffect(() => {
    const fetchDates = async () => {
      const currentValidPickupDates = await getAvailablePickup(shipmentData?.id);
      setValidPickupDates(currentValidPickupDates?.availableDates);
    };
    fetchDates();
  }, [shipmentData?.id]);

  // parse eta time out of date time string to set initial value for time selector
  const parseInitialEtaTime = useCallback((etaDateString, etaTimeSlotIndex) => {
    // Use time slot index if we have it for initial time setting, otherwise fall back to date time logic from before
    if (etaTimeSlotIndex !== null && etaTimeSlotIndex !== undefined) {
      return Number(etaTimeSlotIndex);
    }

    if (!etaDateString) return null;

    const etaDateStart = moment.utc(etaDateString).locale("en").local();

    const etaDateEnd = moment(etaDateStart).add(4, "hours");

    const etaHoursStartMil = etaDateStart.get("hours");
    const etaHoursStart = convertMilHours(etaHoursStartMil);
    const etaHoursEndMil = etaDateEnd.get("hours");
    const etaHoursEnd = convertMilHours(etaHoursEndMil);

    const startAMOrPM = etaHoursStartMil < 12 ? "AM" : "PM";
    const endAMOrPM = etaHoursEndMil < 12 ? "AM" : "PM";

    return `${etaHoursStart}${startAMOrPM} to ${etaHoursEnd}${endAMOrPM}`;
  }, []);

  const renderCalendarFooter = useCallback(
    type => {
      let restrictedDates;

      if (type === CALENDAR_FOOTER.PICKUP) {
        restrictedDates = shipmentData?.pickupAddress?.dayOfWeekRestricted?.split(",");
      } else if (type === CALENDAR_FOOTER.DROPOFF) {
        restrictedDates = shipmentData?.dropOffAddress?.dayOfWeekRestricted?.split(",");
      }

      // Don't render footer if no restrictions set on shipment
      if (!restrictedDates || restrictedDates.length === 0) {
        return null;
      }

      const restrictedDatesString = getRestrictedDaysOfWeekAsString(restrictedDates);

      return <ShipmentEtaCalendarFooter type={type} restrictedDatesString={restrictedDatesString} />;
    },
    [shipmentData],
  );

  const handlePickupEtaDateChange = useCallback(
    async value => {
      if (!value) {
        setPickupEtaDateSet(false);
        formRef?.setFieldsValue({
          pickupEtaTimeSlotIndex: null,
        });
        return;
      }

      setPickupEtaDateSet(true);

      const newSelectedDateIsValid = validPickupDates.some(validDate => validDate.isSame(value, "day"));

      if (!newSelectedDateIsValid) {
        message.warn("Selected date is outside the expected valid range.");
      }

      // Recalc valid dropoff dates if pickup eta selected is changed
      const currentValidDropoffDates = await getAvailableDropoff(shipmentData?.id, value);

      setValidDropoffDates(currentValidDropoffDates?.availableDates);
    },
    [shipmentData?.id, validPickupDates, formRef],
  );

  // Colors dates that are considered "valid" green to make them easily noticeable on the calendar
  const renderValidPickupDates = useCallback(
    current => {
      const currentPickupEtaObject = formRef?.getFieldValue("pickupEtaDate");

      const style = {};

      // If date is part of current valid list, color it green
      if (validPickupDates?.some(validPickupDate => validPickupDate.isSame(current, "day"))) {
        style.backgroundColor = "#f6ffed";
        style.color = "#000000";
      }

      // Color the currently selected date the same blue as the calendar normally does
      if (currentPickupEtaObject?.isSame(current, "day")) {
        style.backgroundColor = "#2d6ac4";
        style.color = "#ffffff";
      }

      return (
        <div className="ant-picker-cell-inner" style={style}>
          {current.date()}
        </div>
      );
    },
    [validPickupDates, formRef],
  );

  const handleDropoffEtaDateChange = useCallback(
    value => {
      if (!value) {
        setDropoffEtaDateSet(false);
        formRef?.setFieldsValue({
          dropoffEtaTimeSlotIndex: null,
        });
        return;
      }

      setDropoffEtaDateSet(true);

      const newSelectedDateIsValid = validDropoffDates.some(validDate => validDate.isSame(value, "day"));

      if (!newSelectedDateIsValid) {
        message.warn("Selected date is outside the expected valid range.");
      }
    },
    [validDropoffDates, formRef],
  );

  // Colors dates that are considered "valid" green to make them easily noticeable on the calendar
  const renderValidDropoffDates = useCallback(
    current => {
      const currentDropoffEtaObject = formRef?.getFieldValue("dropoffEtaDate");

      const style = {};

      // If date is part of current valid list, color it green
      if (validDropoffDates?.some(validDropoffDate => validDropoffDate.isSame(current, "day"))) {
        style.backgroundColor = "#f6ffed";
        style.color = "#000000";
      }

      // Color the currently selected date the same blue as the calendar normally does
      if (currentDropoffEtaObject?.isSame(current, "day")) {
        style.backgroundColor = "#2d6ac4";
        style.color = "#ffffff";
      }

      return (
        <div className="ant-picker-cell-inner" style={style}>
          {current.date()}
        </div>
      );
    },
    [validDropoffDates, formRef],
  );

  const onSubmit = useCallback(
    values => {
      const loadId = shipmentData?.load?.id;

      if (!loadId) return;

      setIsLoadingDelivery(true);

      const { pickupEtaDate, dropoffEtaDate, pickupEtaTimeSlotIndex, dropoffEtaTimeSlotIndex } = values;

      const payload = {
        pickupETA: pickupEtaDate.startOf("day"),
        dropOffETA: dropoffEtaDate.startOf("day"),
        pickupEtaTimeSlot: pickupEtaTimeSlotIndex,
        dropOffEtaTimeSlot: dropoffEtaTimeSlotIndex,
      };

      changeAdminETARequest(loadId, payload)
        .then(() => {
          message.success("ETA dates updated!");
          setIsLoadingDelivery(false);
          if (typeof refreshData === "function") refreshData();
        })
        .catch(err => {
          console.error(err);
          message.error("Could not update ETA");
          setIsLoadingDelivery(false);
        });
    },
    [shipmentData, setIsLoadingDelivery, refreshData],
  );

  return (
    <Form
      layout="vertical"
      form={formRef}
      onFinish={onSubmit}
      initialValues={{
        pickup: shipmentData?.load?.pickUpTime ? moment.utc(shipmentData?.load?.pickUpTime).locale("en").local() : null,
        pickupEtaDate: shipmentData?.load?.pickupETA ? moment.utc(shipmentData?.load?.pickupETA) : null,
        pickupEtaTimeSlotIndex: parseInitialEtaTime(
          shipmentData?.load?.pickupETA,
          shipmentData?.load?.pickupETATimeSlot,
        ),
        dropoff: shipmentData?.load?.dropOffTime
          ? moment.utc(shipmentData?.load?.dropOffTime).locale("en").local()
          : null,
        dropoffEtaDate: shipmentData?.load?.dropOffETA ? moment.utc(shipmentData?.load?.dropOffETA) : null,
        dropoffEtaTimeSlotIndex: parseInitialEtaTime(
          shipmentData?.load?.dropOffETA,
          shipmentData?.load?.dropOffETATimeSlot,
        ),
      }}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="pickupEtaDate"
            label="Pickup ETA"
            style={{
              width: "100%",
            }}
            rules={[
              {
                required: true,
                message: "Please select a pickup date",
              },
            ]}
          >
            <DatePicker
              format="MM/DD/YYYY"
              onChange={handlePickupEtaDateChange}
              renderExtraFooter={() => renderCalendarFooter(CALENDAR_FOOTER.PICKUP)}
              dateRender={renderValidPickupDates}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          <Form.Item
            name="pickupEtaTimeSlotIndex"
            style={{
              width: "100%",
            }}
            rules={[
              {
                required: true,
                message: "Please select a pickup time",
              },
            ]}
          >
            <Select
              placeholder="Select Time"
              disabled={!pickupEtaDateSet}
              style={{
                width: "100%",
              }}
            >
              {Object.values(LoadTimeSlot).map(i => (
                <Select.Option value={Number(i)} key={Number(i)}>
                  {LoadTimeSlotMap[i]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="dropoffEtaDate"
            label="Drop-off ETA"
            style={{
              width: "100%",
            }}
            rules={[
              {
                required: true,
                message: "Please select a dropoff date",
              },
            ]}
          >
            <DatePicker
              format="MM/DD/YYYY"
              onChange={handleDropoffEtaDateChange}
              renderExtraFooter={() => renderCalendarFooter(CALENDAR_FOOTER.DROPOFF)}
              dateRender={renderValidDropoffDates}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          <Form.Item
            name="dropoffEtaTimeSlotIndex"
            style={{
              width: "100%",
            }}
            rules={[
              {
                required: true,
                message: "Please select a dropoff time",
              },
            ]}
          >
            <Select
              placeholder="Select Time"
              disabled={!dropoffEtaDateSet}
              style={{
                width: "100%",
              }}
            >
              {Object.values(LoadTimeSlot).map(i => (
                <Select.Option value={Number(i)} key={Number(i)}>
                  {LoadTimeSlotMap[i]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
